import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import OTPModal from '../../components/auth/OTPModal'
import SuccessModal from '../../components/auth/SuccessModal'
import Header from '../../components/common/Header'

// Registration flow states - same as main registration
const REGISTRATION_STATES = {
  FORM_FILLING: 'form_filling',
  SUBMITTING: 'submitting',
  OTP_PENDING: 'otp_pending',
  OTP_VERIFYING: 'otp_verifying',
  OTP_ERROR: 'otp_error',
  OTP_EXPIRED: 'otp_expired',
  OTP_RESENDING: 'otp_resending',
  VERIFICATION_SUCCESS: 'verification_success',
  COMPLETING_REGISTRATION: 'completing_registration',
  ERROR: 'error'
}

const GoogleRegister = () => {
  const { userType } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  
  // Get Google OAuth data from location state or handle Google OAuth callback
  const googleData = location.state?.googleData
   
  // Main registration state
  const [registrationState, setRegistrationState] = useState(REGISTRATION_STATES.FORM_FILLING)
  
  // Form data - pre-filled from Google data
  const [formData, setFormData] = useState({
    firstName: googleData?.given_name || '',
    lastName: googleData?.family_name || '',
    email: googleData?.email || '',
    mobile: '',
    userType: userType?.toUpperCase() || 'PET_GUARDIAN',
    profileImage: googleData?.picture || null
  })
  
  // UI state
  const [activeTab, setActiveTab] = useState('email') // Default to email for Google registration
  const [errors, setErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState('')
  
  // OTP state
  const [otpData, setOtpData] = useState({
    verificationId: null,
    target: '',
    sessionData: null,
    expiresAt: null,
    attemptsRemaining: 3,
    canResend: false,
    nextResendTime: null
  })
  
  // Countdown timer state
  const [timeRemaining, setTimeRemaining] = useState(0)
  
  // User type configurations - same as main registration
  const userTypeConfig = {
    pet_guardian: {
      title: 'Pet Guardians',
      subtitle: 'For pet parents and guardians',
      primaryColor: '#8548A8',
      mainCircleGradient: 'linear-gradient(135deg, #8548A8 0%, #2A8AD2 100%)',
      floatingCircle1: 'linear-gradient(135deg, #FFA055 0%, #8548A8 100%)',
      floatingCircle2: '#8548A8',
      floatingCircle3: 'linear-gradient(135deg, #FFA055 0%, #FF6B9D 100%)',
      floatingCircle4: '#2A8AD2',
      floatingCircle5: 'rgba(255, 160, 85, 0.6)'
    },
    veterinarian: {
      title: 'Veterinary Professional',
      subtitle: 'For licensed veterinarians and veterinary specialists',
      primaryColor: '#00D4B3',
      mainCircleGradient: 'linear-gradient(135deg, #2A8AD2 0%, #00D483 100%)',
      floatingCircle1: 'linear-gradient(135deg, #00D4B3 0%, #2A8AD2 100%)',
      floatingCircle2: '#00D4B3',
      floatingCircle3: 'linear-gradient(135deg, #2A8AD2 0%, #00D4B3 100%)',
      floatingCircle4: '#2A8AD2',
      floatingCircle5: 'rgba(0, 212, 179, 0.6)'
    },
    service_provider: {
      title: 'Service Provider',
      subtitle: 'For professionals who serve pets and their families',
      primaryColor: '#D65745',
      mainCircleGradient: 'linear-gradient(135deg, #D65745 0%, #00D483 100%)',
      floatingCircle1: 'linear-gradient(135deg, #D65745 0%, #00D4B3 100%)',
      floatingCircle2: '#D65745',
      floatingCircle3: 'linear-gradient(135deg, #00D4B3 0%, #D65745 100%)',
      floatingCircle4: '#00D4B3',
      floatingCircle5: 'rgba(214, 87, 69, 0.6)'
    },
    firm: {
      title: 'Firm/Company',
      subtitle: 'For clinics, hospitals, and pet-related businesses',
      primaryColor: '#000080',
      mainCircleGradient: 'linear-gradient(135deg, #000080 0%, #2A8AD2 100%)',
      floatingCircle1: 'linear-gradient(135deg, #000080 0%, #FFA055 100%)',
      floatingCircle2: '#000080',
      floatingCircle3: 'linear-gradient(135deg, #2A8AD2 0%, #000080 100%)',
      floatingCircle4: '#2A8AD2',
      floatingCircle5: 'rgba(0, 0, 128, 0.6)'
    }
  }
  
  const config = userTypeConfig[userType] || userTypeConfig.pet_guardian
  
  // Check if Google data exists, redirect if not
  useEffect(() => {
    if (!googleData) {
      console.error('No Google data found. Redirecting to registration.')
      navigate(`/auth/register/${userType}`)
    }
  }, [googleData, navigate, userType])
  
  // Timer logic for OTP expiration
  useEffect(() => {
    let interval
    
    if (timeRemaining > 0 && (registrationState === REGISTRATION_STATES.OTP_PENDING || 
                             registrationState === REGISTRATION_STATES.OTP_ERROR)) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setRegistrationState(REGISTRATION_STATES.OTP_EXPIRED)
            setOtpData(prev => ({ ...prev, canResend: true }))
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else {
      clearInterval(interval)
    }
    
    return () => clearInterval(interval)
  }, [timeRemaining, registrationState])
  
  // Auto-clear success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])
  
  // Form validation
  const validateForm = useCallback(() => {
    const newErrors = {}
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters'
    }
    
    if (!formData.lastName.trim()) {
      if (userType !== 'firm') {
        newErrors.lastName = 'Last name is required'
      }
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters'
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address'
      }
    }
    
    // Mobile is optional for Google registration
    if (activeTab === 'mobile' && formData.mobile && formData.mobile.length < 10) {
      newErrors.mobile = 'Please enter a valid mobile number'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData, activeTab])
  
  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target
    let finalValue = value
    if (name === 'mobile') {
      finalValue = value.replace(/[^0-9\+\-\s]/g, '')
    }
    setFormData(prev => ({ ...prev, [name]: finalValue }))
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }, [errors])
  
  // Handle tab change
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab)
    setErrors({})
  }, [])
  
  // Error handling
  const handleApiError = useCallback((status, data) => {
    setRegistrationState(REGISTRATION_STATES.ERROR)
    
    if (status === 400) {
      if (data.detail?.includes('Email already registered')) {
        setErrors({ email: 'This email is already registered. Please try logging in instead.' })
      } else if (data.detail?.includes('Phone already registered')) {
        setErrors({ mobile: 'This phone number is already registered. Please try logging in instead.' })
      } else {
        setErrors({ general: data.detail || 'Registration failed. Please try again.' })
      }
    } else if (status === 422) {
      setErrors({ general: 'Please check your information and try again.' })
    } else if (status === 429) {
      setErrors({ general: 'Too many registration attempts. Please try again later.' })
    } else {
      setErrors({ general: data.detail || 'Registration failed. Please try again.' })
    }
  }, [])
  
  // Google registration initiation (creates user and sends OTP for additional verification)
  const initiateGoogleRegistration = useCallback(async () => {
    if (!validateForm()) return
    
    setRegistrationState(REGISTRATION_STATES.SUBMITTING)
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      
      const userTypeMapping = {
        'pet_guardian': 'PET_GUARDIAN',
        'veterinarian': 'VETERINARIAN',
        'service_provider': 'SERVICE_PROVIDER',
        'firm': 'FIRM'
      }
      
      const backendUserType = userTypeMapping[userType]
      
      if (!backendUserType) {
        throw new Error('Invalid user type selected')
      }
      
      // Prepare social login data for backend
      const socialRegistrationData = {
        provider: 'google',
        provider_user_id: googleData.sub || googleData.id,
        access_token: googleData.access_token,
        provider_email: formData.email.trim(),
        provider_profile_data: {
          ...googleData,
          // Override with form data
          given_name: formData.firstName.trim(),
          family_name: formData.lastName.trim(),
          email: formData.email.trim()
        },
        user_type: backendUserType,
        additional_info: {
          ...(activeTab === 'mobile' && formData.mobile && { phone: formData.mobile.trim() }),
          language_preference: 'en'
        }
      }
      
    
      // First, attempt social login to see if user already exists
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/social/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(socialRegistrationData)
      })
    
      const data = await response.json()
      
      if (response.ok) {
        // User already exists and logged in successfully
        // Store authentication tokens
        if (data.access_token) {
          localStorage.setItem('access_token', data.access_token)
        }
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token)
        }
        if (data.user) {
          localStorage.setItem('user_data', JSON.stringify(data.user))
        }
        
        
        // User logged in successfully
        setTimeout(() => {
          // Use a custom event to trigger token sending
          window.dispatchEvent(new CustomEvent('user-logged-in'));
        }, 1000);
        
        // Navigate to dashboard or complete profile based on user status
        navigate('/dashboard', {
          state: {
            user: data.user,
            loginMethod: 'google',
            isExistingUser: true
          }
        })
        
      } else if (response.status === 404 || data.detail?.includes('not found')) {
        // User doesn't exist, proceed with registration flow
        // For Google registration, we might want to send an OTP for additional verification
        // This simulates the same flow as regular registration
        
        if (activeTab === 'mobile' && formData.mobile) {
          // Send SMS OTP
          const otpResponse = await fetch(`${API_BASE_URL}/api/v1/auth/register/initiate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              user_type: backendUserType,
              first_name: formData.firstName.trim(),
              ...(userType !== 'firm' && { last_name: formData.lastName.trim() }),
              phone: formData.mobile.trim(),
              language_preference: 'en',
              social_data: socialRegistrationData
            })
          })
          
          const otpData = await otpResponse.json()
          
          if (otpResponse.ok) {
            // Handle OTP flow
            setOtpData({
              verificationId: otpData.verification_id,
              target: otpData.target,
              sessionData: { ...otpData, socialData: socialRegistrationData },
              expiresAt: new Date(Date.now() + (otpData.expires_in_minutes * 60 * 1000)),
              attemptsRemaining: 3,
              canResend: false,
              nextResendTime: new Date(Date.now() + 60000)
            })
            
            setTimeRemaining(otpData.expires_in_minutes * 60)
            setRegistrationState(REGISTRATION_STATES.OTP_PENDING)
            setErrors({})
          } else {
            handleApiError(otpResponse.status, otpData)
          }
        } else {
          // Email-only registration - directly complete social registration
          const directRegResponse = await fetch(`${API_BASE_URL}/api/v1/auth/social/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...socialRegistrationData,
              user_type: backendUserType,
              first_name: formData.firstName.trim(),
              ...(userType !== 'firm' && { last_name: formData.lastName.trim() }),
              language_preference: 'en'
            })
          })
          
          const directRegData = await directRegResponse.json()
          
          if (directRegResponse.ok) {
            // Store authentication tokens
            if (directRegData.access_token) {
              localStorage.setItem('access_token', directRegData.access_token)
            }
            if (directRegData.refresh_token) {
              localStorage.setItem('refresh_token', directRegData.refresh_token)
            }
            if (directRegData.user) {
              localStorage.setItem('user_data', JSON.stringify(directRegData.user))
            }
            
            
            // User registered successfully
            setTimeout(() => {
              // Use a custom event to trigger token sending
              window.dispatchEvent(new CustomEvent('user-logged-in'));
            }, 1000);
            
            // Navigate to profile completion
            navigate(`/auth/complete-profile/${userType}`, {
              state: {
                user: directRegData.user,
                registrationMethod: 'google',
                autoLoggedIn: true
              }
            })
          } else {
            handleApiError(directRegResponse.status, directRegData)
          }
        }
      } else {
        // Handle other errors
        handleApiError(response.status, data)
      }
    } catch (error) {
      console.error('Google registration error:', error)
      setErrors({ general: 'Network error. Please check your connection and try again.' })
      setRegistrationState(REGISTRATION_STATES.ERROR)
    }
  }, [formData, activeTab, userType, googleData, validateForm, handleApiError, navigate])
  
  // OTP verification
  const verifyOTP = useCallback(async (code) => {
    setRegistrationState(REGISTRATION_STATES.OTP_VERIFYING)
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/register/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verification_id: otpData.verificationId,
          code: code
        })
      })
      
      const data = await response.json()
      
      if (response.ok) {
        // OTP verified, now complete Google registration
        const socialRegResponse = await fetch(`${API_BASE_URL}/api/v1/auth/social/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...otpData.sessionData.socialData,
            verification_id: otpData.verificationId
          })
        })
        
        const socialRegData = await socialRegResponse.json()
        
        if (socialRegResponse.ok) {
          // Store authentication tokens
          if (socialRegData.access_token) {
            localStorage.setItem('access_token', socialRegData.access_token)
          }
          if (socialRegData.refresh_token) {
            localStorage.setItem('refresh_token', socialRegData.refresh_token)
          }
          if (socialRegData.user) {
            localStorage.setItem('user_data', JSON.stringify(socialRegData.user))
          }
          
          setRegistrationState(REGISTRATION_STATES.VERIFICATION_SUCCESS)
        } else {
          setErrors({ otp: socialRegData.detail || 'Registration failed after OTP verification.' })
          setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
        }
      } else {
        setOtpData(prev => ({
          ...prev,
          attemptsRemaining: Math.max(0, prev.attemptsRemaining - 1)
        }))
        
        setErrors({ otp: data.detail || 'Invalid verification code. Please try again.' })
        setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
      }
    } catch (error) {
      console.error('OTP verification error:', error)
      setErrors({ otp: 'Network error. Please check your connection and try again.' })
      setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
    }
  }, [otpData])
  
  // Resend OTP
  const resendOTP = useCallback(async () => {
    setRegistrationState(REGISTRATION_STATES.OTP_RESENDING)
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/register/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verification_id: otpData.verificationId
        })
      })
      
      const data = await response.json()
      
      if (response.ok) {
        const expiresAt = new Date(Date.now() + ((data.expires_in_minutes || 10) * 60 * 1000))
        
        setOtpData(prev => ({
          ...prev,
          verificationId: data.verification_id || prev.verificationId,
          expiresAt,
          attemptsRemaining: 3,
          canResend: false,
          nextResendTime: new Date(Date.now() + 60000)
        }))
        
        setTimeRemaining((data.expires_in_minutes || 10) * 60)
        setRegistrationState(REGISTRATION_STATES.OTP_PENDING)
        setSuccessMessage('New verification code sent!')
        setErrors({})
      } else {
        setErrors({ otp: data.detail || 'Failed to resend code.' })
        setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
      }
    } catch (error) {
      console.error('OTP resend error:', error)
      setErrors({ otp: 'Network error. Please try again.' })
      setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
    }
  }, [otpData.verificationId])

  // Event handlers
  const handleSubmit = useCallback(() => {
    initiateGoogleRegistration()
  }, [initiateGoogleRegistration])
  
  const handleOTPVerify = useCallback((code) => {
    verifyOTP(code)
  }, [verifyOTP])
  
  const handleOTPResend = useCallback(() => {
    resendOTP()
  }, [resendOTP])
  
  const handleSuccessContinue = useCallback(() => {
    setRegistrationState(REGISTRATION_STATES.COMPLETING_REGISTRATION)
    
    // Navigate to profile completion
    navigate(`/auth/complete-profile/${userType}`, {
      state: {
        user: JSON.parse(localStorage.getItem('user_data') || '{}'),
        registrationMethod: 'google',
        autoLoggedIn: true
      }
    })
  }, [navigate, userType])

  // Computed values
  const isFormLoading = registrationState === REGISTRATION_STATES.SUBMITTING
  const showOTPModal = [
    REGISTRATION_STATES.OTP_PENDING,
    REGISTRATION_STATES.OTP_VERIFYING,
    REGISTRATION_STATES.OTP_ERROR,
    REGISTRATION_STATES.OTP_EXPIRED,
    REGISTRATION_STATES.OTP_RESENDING
  ].includes(registrationState)
  
  const showSuccessModal = registrationState === REGISTRATION_STATES.VERIFICATION_SUCCESS
  
  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: '#FAFAFA',
      position: 'fixed',
      top: 0,
      left: 0,
      fontFamily: '"Red Hat Display", sans-serif',
      overflow: 'hidden',
      display: 'flex'
    }}>
      {/* Header Component */}
      <Header />
      
      {/* Left Section - Design Panel */}
      <div style={{
        width: '50%',
        height: '100vh',
        background: '#FEFEFA',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '140px 60px 60px 60px',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Main Circle with Character */}
        <div style={{
          position: 'relative',
          width: '540px',
          height: '540px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2
        }}>
          <img
            src={`/character/${userType === 'pet_guardian' ? 'register-petowner.png' : 
                            userType === 'veterinarian' ? 'register-vet.png' : 
                            userType === 'firm' ? 'business.png' :
                            'register-service.png'}`}
            alt={`${config.title} character`}
            style={{
              position: 'relative',
              width: '540px',
              height: '540px',
              objectFit: 'contain',
              zIndex: 2
            }}
            onError={(e) => {
              console.error(`Failed to load image: ${e.target.src}`)
              e.target.style.display = 'none'
            }}
          />
        </div>
        
        {/* Floating Decorative Circles */}
        <div style={{
          position: 'absolute',
          top: '80px',
          left: '40px',
          width: '125px',
          height: '123px',
          background: config.floatingCircle1,
          borderRadius: '50%',
          opacity: 0.6,
          zIndex: 0
        }}></div>
        
        <div style={{
          position: 'absolute',
          top: '120px',
          right: '80px',
          width: '80px',
          height: '80px',
          background: config.floatingCircle2,
          borderRadius: '50%',
          opacity: 0.8,
          zIndex: 0
        }}></div>
        
        <div style={{
          position: 'absolute',
          bottom: '100px',
          left: '20px',
          width: '160px',
          height: '160px',
          background: config.floatingCircle3,
          borderRadius: '50%',
          opacity: 0.5,
          zIndex: 0
        }}></div>
        
        <div style={{
          position: 'absolute',
          bottom: '180px',
          right: '60px',
          width: '100px',
          height: '100px',
          background: config.floatingCircle4,
          borderRadius: '50%',
          opacity: 0.7,
          zIndex: 0
        }}></div>
        
        <div style={{
          position: 'absolute',
          top: '300px',
          left: '0px',
          width: '60px',
          height: '60px',
          background: config.floatingCircle5,
          borderRadius: '50%',
          opacity: 0.4,
          zIndex: 0
        }}></div>
      </div>
      
      {/* Right Section - Registration Form */}
      <div style={{
        width: '50%',
        height: '100vh',
        background: '#FFFFFF',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'auto'
      }}>
        {/* Progress Steps */}
        <div style={{
          position: 'absolute',
          top: '140px',
          left: '0',
          right: '0',
          width: '100%',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 80px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '600px'
          }}>
            {/* Step 1 - Active */}
            <div style={{
              width: '40px',
              height: '40px',
              background: config.primaryColor,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '18px',
              color: '#FFFFFF',
              zIndex: 2
            }}>
              1
            </div>

            {/* Connecting Line 1-2 */}
            <div style={{
              flex: 1,
              height: '3px',
              background: '#E5E7EB',
              margin: '0 20px'
            }} />

            {/* Step 2 - Inactive */}
            <div style={{
              width: '40px',
              height: '40px',
              background: '#E5E7EB',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '18px',
              color: '#9CA3AF',
              zIndex: 2
            }}>
              2
            </div>

            {/* Connecting Line 2-3 */}
            <div style={{
              flex: 1,
              height: '3px',
              background: '#E5E7EB',
              margin: '0 20px'
            }} />

            {/* Step 3 - Inactive */}
            <div style={{
              width: '40px',
              height: '40px',
              background: '#E5E7EB',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '18px',
              color: '#9CA3AF',
              zIndex: 2
            }}>
              3
            </div>
          </div>
        </div>
        
        {/* Form Content Container */}
        <div style={{
          position: 'absolute',
          top: '260px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: '600px',
          padding: '0 80px',
          boxSizing: 'border-box'
        }}>
          {/* Form Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '48px'
          }}>
            <h2 style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '38px',
              lineHeight: '1.3',
              color: '#1F2937',
              margin: '0 0 12px 0'
            }}>
              Complete Registration
            </h2>
            <p style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 400,
              fontSize: '18px',
              lineHeight: '1.5',
              color: '#6B7280',
              margin: 0
            }}>
              Continue with Google account
            </p>
          </div>
          
          {/* Google Account Info */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '16px',
            background: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            marginBottom: '24px'
          }}>
            {formData.profileImage && (
              <img 
                src={formData.profileImage}
                alt="Google profile"
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
              />
            )}
            <div style={{ flex: 1 }}>
              <p style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                color: '#6B7280',
                margin: '0 0 4px 0'
              }}>
                Continuing with Google account
              </p>
              <p style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '16px',
                fontWeight: 500,
                color: '#1F2937',
                margin: 0
              }}>
                {formData.email}
              </p>
            </div>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </div>
          
          {/* Error Message */}
          {(errors.general || errors.email || errors.mobile) && (
            <div style={{
              padding: '12px',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <p style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                color: '#EF4444',
                margin: 0
              }}>
                {errors.general || errors.email || errors.mobile}
              </p>
            </div>
          )}
          
          {/* Name Fields */}
          <div style={{
            display: 'flex',
            gap: '20px',
            marginBottom: '32px'
          }}>
            <div style={{ flex: 1 }}>
              <label style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: '#374151',
                display: 'block',
                marginBottom: '10px'
              }}>
                First name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Enter first name"
                disabled={isFormLoading}
                style={{
                  width: '100%',
                  height: '56px',
                  padding: '0 20px',
                  border: `1.5px solid ${errors.firstName ? '#EF4444' : '#E5E7EB'}`,
                  borderRadius: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '16px',
                  color: '#1F2937',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  background: isFormLoading ? '#F9FAFB' : '#FFFFFF',
                  cursor: isFormLoading ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => !isFormLoading && (e.target.style.borderColor = config.primaryColor)}
                onBlur={(e) => !isFormLoading && (e.target.style.borderColor = errors.firstName ? '#EF4444' : '#E5E7EB')}
              />
              {errors.firstName && (
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  color: '#EF4444',
                  margin: '4px 0 0 0'
                }}>
                  {errors.firstName}
                </p>
              )}
            </div>
            
            <div style={{ flex: 1 }}>
              <label style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: '#374151',
                display: 'block',
                marginBottom: '10px'
              }}>
                Last name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Enter last name"
                disabled={isFormLoading}
                style={{
                  width: '100%',
                  height: '56px',
                  padding: '0 20px',
                  border: `1.5px solid ${errors.lastName ? '#EF4444' : '#E5E7EB'}`,
                  borderRadius: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '16px',
                  color: '#1F2937',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  background: isFormLoading ? '#F9FAFB' : '#FFFFFF',
                  cursor: isFormLoading ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => !isFormLoading && (e.target.style.borderColor = config.primaryColor)}
                onBlur={(e) => !isFormLoading && (e.target.style.borderColor = errors.lastName ? '#EF4444' : '#E5E7EB')}
              />
              {errors.lastName && (
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  color: '#EF4444',
                  margin: '4px 0 0 0'
                }}>
                  {errors.lastName}
                </p>
              )}
            </div>
          </div>
          
          {/* Email Field (Pre-filled from Google) */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              color: '#374151',
              display: 'block',
              marginBottom: '10px'
            }}>
              Email
            </label>
            <div style={{
              position: 'relative',
              width: '100%'
            }}>
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none"
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2
                }}
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="22,6 12,13 2,6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email"
                disabled={true} // Email is disabled as it comes from Google
                style={{
                  width: '100%',
                  height: '56px',
                  padding: '0 20px 0 50px',
                  border: '1.5px solid #E5E7EB',
                  borderRadius: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '16px',
                  color: '#9CA3AF',
                  boxSizing: 'border-box',
                  outline: 'none',
                  background: '#F9FAFB',
                  cursor: 'not-allowed'
                }}
              />
            </div>
            <p style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '12px',
              color: '#6B7280',
              margin: '4px 0 0 0'
            }}>
              Email verified through Google
            </p>
          </div>
          
          {/* Optional Mobile Number */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              color: '#374151',
              display: 'block',
              marginBottom: '10px'
            }}>
              Mobile Number (Optional)
            </label>
            <div style={{
              position: 'relative',
              width: '100%'
            }}>
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none"
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2
                }}
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                placeholder="Mobile (Optional)"
                disabled={isFormLoading}
                style={{
                  width: '100%',
                  height: '56px',
                  padding: '0 20px 0 50px',
                  border: `1.5px solid ${errors.mobile ? '#EF4444' : '#E5E7EB'}`,
                  borderRadius: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '16px',
                  color: '#1F2937',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  background: isFormLoading ? '#F9FAFB' : '#FFFFFF',
                  cursor: isFormLoading ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => !isFormLoading && (e.target.style.borderColor = config.primaryColor)}
                onBlur={(e) => !isFormLoading && (e.target.style.borderColor = errors.mobile ? '#EF4444' : '#E5E7EB')}
              />
              {errors.mobile && (
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  color: '#EF4444',
                  margin: '4px 0 0 0'
                }}>
                  {errors.mobile}
                </p>
              )}
            </div>
            <p style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '12px',
              color: '#6B7280',
              margin: '4px 0 0 0'
            }}>
              {formData.mobile ? 'SMS verification required if provided' : 'Add mobile for SMS notifications'}
            </p>
          </div>
          
          {/* Submit Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '30px'
          }}>
            <button
              onClick={handleSubmit}
              disabled={isFormLoading}
              style={{
                width: '240px',
                height: '56px',
                background: isFormLoading ? '#E5E7EB' : config.primaryColor,
                borderRadius: '14px',
                border: 'none',
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: '18px',
                color: isFormLoading ? '#9CA3AF' : '#FFFFFF',
                cursor: isFormLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!isFormLoading) {
                  e.target.style.transform = 'translateY(-1px)'
                  e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)'
                }
              }}
              onMouseLeave={(e) => {
                if (!isFormLoading) {
                  e.target.style.transform = 'translateY(0)'
                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'
                }
              }}
            >
              {isFormLoading && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{
                  animation: 'spin 1s linear infinite'
                }}>
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              )}
              {isFormLoading ? 'Creating Account...' : 'Complete Registration'}
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
            </button>
          </div>
          
          {/* Back Link */}
          <div style={{
            textAlign: 'center'
          }}>
            <p style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '14px',
              color: '#9CA3AF',
              margin: 0
            }}>
              Want to use a different account?{' '}
              <span
                onClick={() => !isFormLoading && navigate(`/auth/register/${userType}`)}
                style={{
                  color: config.primaryColor,
                  cursor: isFormLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  opacity: isFormLoading ? 0.5 : 1
                }}
              >
                Back to Registration
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      {showOTPModal && (
        <OTPModal
          isOpen={true}
          primaryColor={config.primaryColor}
          target={otpData.target}
          timeRemaining={timeRemaining}
          attemptsRemaining={otpData.attemptsRemaining}
          canResend={otpData.canResend}
          isVerifying={registrationState === REGISTRATION_STATES.OTP_VERIFYING}
          isResending={registrationState === REGISTRATION_STATES.OTP_RESENDING}
          error={errors.otp}
          onVerify={handleOTPVerify}
          onResend={handleOTPResend}
          onClose={() => {/* handle close */}}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal
          isOpen={true}
          title={t('register.oauth.google.title')}
          message={t('register.oauth.google.message')}
          buttonText={t('register.success.button')}
          loadingText={t('register.success.loading')}
          target={otpData.target}
          variant="registration"
          onContinue={handleSuccessContinue}
        />
      )}
    </div>
  )
}

export default GoogleRegister